import React, { useEffect, useState, useMemo } from 'react';
import { Table, Input, Button, Pagination, Tag, message, Dropdown, Radio, Menu, Modal, Form, Select, Descriptions, Table as AntTable, Tooltip } from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  DownOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import OrderApiService from '../../services/OrderApiService';
import '../../styles/OrderManagement.css';
import { use } from 'react';
import { debounce } from '../../utils/debounce';
import OrderDetailModal from '../../components/orders/OrderDetailModal';
import UpdateOrderModal from '../../components/orders/UpdateOrderModal';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../constants/Role';

const { Search } = Input;

const OrderManagement = () => {
  // Auth context
  const { hasAnyRole } = useAuth();
  
  // Check permissions
  const isAdminDev = hasAnyRole([Role.ADMIN_DEV]);
  const isAdminBusiness = hasAnyRole([Role.ADMIN_BUSINESS]);
  const isStaffAccounting = hasAnyRole([Role.STAFF_ACCOUNTING]);
  
  // Determine if user can perform update operations
  const canUpdate = isAdminDev || isStaffAccounting;
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sorter, setSorter] = useState({ field: 'createdAt', order: 'descend' });
  const [statusFilter, setStatusFilter] = useState(null);
  const [filters, setFilters] = useState({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [updateOrder, setUpdateOrder] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Debounce search
  const debouncedSearch = useMemo(() => debounce((value) => {
    setSearch(value);
    setPage(1);
  }, 500), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    // Nếu chỉ toàn khoảng trắng thì không search
    if (value.trim() === '' && value.length > 0) {
      return;
    }
    if (value !== value.trim()) {
      debouncedSearch(value.trim());
    } else {
      debouncedSearch(value);
    }
  };

  const handleTableChange = (pagination, tableFilters, sorterObj) => {
    // Multiple sort handling
    let sortField = 'createdAt';
    let sortOrder = 'descend';
    if (Array.isArray(sorterObj)) {
      // Nếu AntD Table ở chế độ multi-sort
      if (sorterObj.length > 0 && sorterObj[0].field) {
        sortField = sorterObj[0].field;
        sortOrder = sorterObj[0].order === 'ascend' ? 'ascend' : 'descend';
      }
    } else if (sorterObj && sorterObj.field) {
      sortField = sorterObj.field;
      sortOrder = sorterObj.order === 'ascend' ? 'ascend' : 'descend';
    }
    setSorter({ field: sortField, order: sortOrder });

    // Gộp các filter lại
    const newFilters = { ...filters };
    if (tableFilters.status !== undefined) {
      newFilters.status = tableFilters.status && tableFilters.status.length > 0 ? tableFilters.status[0] : null;
    }
    if (tableFilters.learnerName !== undefined) {
      newFilters.learnerName = tableFilters.learnerName && tableFilters.learnerName.length > 0 ? tableFilters.learnerName[0] : null;
    }
    setFilters(newFilters);
    setStatusFilter(newFilters.status ? [newFilters.status] : null);
    setPage(1);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: search || undefined,
        sort: `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`,
        status: filters.status || undefined,
        learnerName: filters.learnerName || undefined,
      };
      const res = await OrderApiService.getOrders(params);
      if (res.data && res.data.success) {
        setOrders(res.data.data.orders);
        setTotal(res.data.data.pagination.totalItems);
      } else {
        setOrders([]);
        setTotal(0);
      }
    } catch (err) {
      message.error('Failed to fetch orders');
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, [search, page, pageSize, sorter, filters]);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const handleUpdate = async (record) => {
    setUpdateLoading(true);
    setUpdateModalVisible(true);
    try {
      const res = await OrderApiService.getOrderById(record._id);
      if (res.data && res.data.success) {
        setUpdateOrder(res.data.data);
      } else {
        setUpdateOrder(record);
      }
    } catch (err) {
      setUpdateOrder(record);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setUpdateModalVisible(false);
    setUpdateOrder(null);
  };

  const handleUpdateOrder = async (values) => {
    setUpdateLoading(true);
    try {
      // Nếu status hiện tại là 'pending' thì cho phép đổi, còn lại giữ nguyên
      const currentStatus = updateOrder?.status;
      const statusToSend = currentStatus === 'pending' ? values.status : currentStatus;
      const res = await OrderApiService.updateOrderStatus(updateOrder._id, {
        status: statusToSend,
        note: values.note || '',
      });
      if (res.data && res.data.success) {
        message.success('Order updated successfully');
        setUpdateOrder(res.data.data);
        if (orderDetail && orderDetail._id === res.data.data._id) {
          setOrderDetail(res.data.data);
        }
        setUpdateModalVisible(false);
        setUpdateOrder(null);
        fetchOrders();
      } else {
        message.error('Failed to update order');
      }
    } catch (err) {
      message.error('Failed to update order');
    } finally {
      setUpdateLoading(false);
    }
  };

  const actionMenu = (record, onView, onUpdate) => {
    const menuItems = [
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => onView(record)}>
        View Detail
      </Menu.Item>
    ];

    // Only add update option if user has permission
    if (canUpdate) {
      menuItems.push(
        <Menu.Item key="update" icon={<EditOutlined />} onClick={() => onUpdate(record)}>
          Update
        </Menu.Item>
      );
    }

    return <Menu>{menuItems}</Menu>;
  };

  const handleViewDetail = async (record) => {
    setDetailModalVisible(true);
    setDetailLoading(true);
    try {
      const res = await OrderApiService.getOrderById(record._id);
      if (res.data && res.data.success) {
        setOrderDetail(res.data.data);
      } else {
        setOrderDetail(null);
      }
    } catch (err) {
      setOrderDetail(null);
      message.error('Failed to fetch order detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalVisible(false);
    setOrderDetail(null);
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'no',
      width: 60,
      align: 'center',
      render: (text, record, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: 'Order Code',
      dataIndex: 'shortCode',
      sorter: true,
      sortOrder: sorter.field === 'shortCode' ? sorter.order : null,
      width: 120,
      render: (text) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 120 }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Learner Name',
      dataIndex: 'learnerName',
      sorter: true,
      sortOrder: sorter.field === 'learnerName' ? sorter.order : null,
      width: 150,
      render: (text) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150 }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      sorter: true,
      sortOrder: sorter.field === 'totalAmount' ? sorter.order : null,
      width: 120,
      render: (text) => text ? text.toLocaleString() + '₫' : '',
    },
    {
      title: 'Purchased Date',
      dataIndex: 'purchaseDate',
      sorter: true,
      sortOrder: sorter.field === 'purchaseDate' ? sorter.order : null,
      width: 120,
      render: (text) => {
        if (!text) return '';
        const d = new Date(text);
        return d.toLocaleDateString('vi-VN');
      },
    },
    {
      title: 'Note',
      dataIndex: 'note',
      width: 160,
      render: (text, record) => {
        const maxLen = 50;
        const note = record.note || '';
        const displayText = note.length > maxLen ? note.slice(0, maxLen) + '...' : note;
        return note.trim() !== '' ? (
          <Tooltip title={note} placement="topLeft">
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {displayText}
            </div>
          </Tooltip>
        ) : (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200 }}>-</div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Radio.Group
            value={selectedKeys[0] || null}
            onChange={e => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            <Radio value={null}>All</Radio>
            <Radio value="pending">Pending</Radio>
            <Radio value="completed">Completed</Radio>
            <Radio value="failed">Failed</Radio>
            <Radio value="cancelled">Cancelled</Radio>
          </Radio.Group>
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button
              type="primary"
              size="small"
              onClick={confirm}
              style={{ marginRight: 8 }}
            >
              OK
            </Button>
            <Button
              size="small"
              onClick={() => { clearFilters(); confirm(); }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filteredValue: statusFilter || null,
      onFilter: (value, record) => !value || record.status === value,
      render: (status) => (
        <span className={`status-tag ${status}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 110,
      render: (_, record) => (
        <Dropdown overlay={actionMenu(record, handleViewDetail, handleUpdate)} trigger={["click"]}>
          <Button>
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="order-management-container">
      <h1 className='admin-title'>Orders Management</h1>
      <div className="order-management-search">
        <Search
          placeholder="Search content.."
          allowClear
          value={searchInput}
          onChange={handleSearchChange}
          style={{ width: 500 }}
        />
      </div>
      <Table
      className="striped-table"
        columns={columns}
        dataSource={orders}
        loading={loading}
        pagination={false}
        rowKey="_id"
        bordered
        style={{ marginTop: 16 }}
        onChange={handleTableChange}
        scroll={{ x: 970 }}
      />
      <div className="order-management-pagination">
        <Pagination
          current={page}
          total={total}
          pageSize={pageSize}
          showTotal={(total) => `Total ${total} orders`}
          onChange={setPage}
          showSizeChanger={false}
          style={{ marginTop: 16 }}
        />
      </div>
      <OrderDetailModal
        open={detailModalVisible}
        loading={detailLoading}
        orderDetail={orderDetail}
        onClose={handleCloseDetailModal}
      />
      <UpdateOrderModal
        open={updateModalVisible}
        loading={updateLoading}
        order={updateOrder}
        onClose={handleCloseUpdateModal}
        onUpdate={handleUpdateOrder}
      />
    </div>
  );
};

export default OrderManagement; 